import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { Icon } from "@iconify/react";
import axios from "axios";
import { buildGameStartPayload } from "../utils/gameStartPayload";

// Beautiful Toast Notification Component
const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      case "success":
        return "✅";
      case "info":
        return "ℹ️";
      default:
        return "⚠️";
    }
  };

  const getColors = () => {
    switch (type) {
      case "warning":
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
      case "error":
        return { bg: "#FEE2E2", border: "#EF4444", text: "#991B1B" };
      case "success":
        return { bg: "#D1FAE5", border: "#10B981", text: "#065F46" };
      case "info":
        return { bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" };
      default:
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "400px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{getIcon()}</span>
      <span style={{ color: colors.text, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: colors.text,
          padding: "4px",
          marginLeft: "8px",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

// Use API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let liveCasinoGamesFetched = false;

const remoteProviderPrefixes = [
  "bs",
  "ng",
  "aws",
  "swg",
  "km",
  "fc",
  "mg",
  "gamatron",
  "ep",
  "pp",
  "netent",
  "joker",
  "bpg",
  "bng",
  "hab",
  "kgl",
  "rlx",
  "ygg",
  "red",
  "qs",
  "ids",
  "tk",
  "ds",
  "nlc",
  "ga",
  "png",
  "pug",
  "fng",
  "nge",
  "1x2",
  "hak",
  "waz",
  "elk",
  "prs",
];

const LiveCasino = () => {
  const dispatch = useDispatch();
  const { gameList } = useSelector((state) => state.games);
  const { singleUsers } = useSelector((state) => state.user);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const currentUser = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];
  const isTypeAllowed =
    typeAllowanceList.includes("allgametype") ||
    typeAllowanceList.includes("livecasino") ||
    typeAllowanceList.includes("casino");

  const isProviderAllowed = (prov) => {
    if (!currentUser) return true;
    if (providerAllowanceList === null) return true;
    const id = (
      prov.productId ||
      prov.game ||
      prov.productName ||
      prov.name ||
      ""
    ).toLowerCase();
    return (
      providerAllowanceList.includes(id) ||
      providerAllowanceList.includes(
        (prov.productName || prov.name || "").toLowerCase(),
      )
    );
  };

  const handleProviderClick = (prov) => {
    // If user is not logged in show toast and do not open login modal
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowed) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์คาสิโนนี้ได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    if (isProviderAllowed(prov)) {
      loadProviderGames(prov);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  const [loading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error] = useState(null);

  // New state: manage selected provider and provider games
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerGames, setProviderGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  // Loading while starting game
  const [gameStartLoading, setGameStartLoading] = useState(false);
  const playerBalance = useSelector((state) => state.user.playerBalance);

  useEffect(() => {
    if (liveCasinoGamesFetched) return;
    liveCasinoGamesFetched = true;
    dispatch(fetchGames());
  }, [dispatch]);

  // Load games for a provider from API (GET with providerId in path)
  const loadProviderGames = async (provider) => {
    const providerCode = (
      provider?.game ||
      provider?.productId ||
      ""
    ).toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldUseRemote =
      remoteProviderPrefixes.includes(providerCode) ||
      remoteProviderPrefixes.includes(providerName);

    if (!shouldUseRemote) {
      setSelectedProvider(null);
      setIsLoadingGames(false);
      setGamesError(null);
      setProviderGames([]);
      return getGameLink(provider);
    }

    setSelectedProvider(provider);
    setIsLoadingGames(true);
    setGamesError(null);

    try {
      const res = await axios({
        method: "GET",
        url: `https://space.kuga59.co/games/${providerCode}.json`,
        responseType: "json",
        maxBodyLength: Infinity,
      });
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      if (Array.isArray(list)) {
        setProviderGames(list);
        setIsLoadingGames(false);
        return;
      }
    } catch (err) {
      console.warn(
        "Failed to load games from list source, fallback to API",
        err,
      );
    }

    const params = {
      providerPrefix: providerCode,
      categories: "",
      page: 1,
      perPage: 40,
    };

    try {
      const res = await axios({
        method: "GET",
        url: `${API_BASE_URL}/gamelogin/getgamelist`,
        params,
        maxBodyLength: Infinity,
      });
      const list = res?.data?.gamesList
        ? res.data.gamesList
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.games)
            ? res.data.games
            : [];
      setProviderGames(list || []);
    } catch (error) {
      console.error(error);
      setGamesError("ไม่สามารถโหลดรายการเกมได้");
      setProviderGames([]);
    } finally {
      setIsLoadingGames(false);
    }
  };

  const backToProviders = () => {
    setSelectedProvider(null);
    setProviderGames([]);
    setGamesError(null);
  };

  // Start game in a new window
  const getGameLink = async (request) => {
    setGameStartLoading(true);

    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch (_) {}

    const data = { id: userId };

    if (selectedProvider) {
      data.provider =
        selectedProvider?.productId ||
        selectedProvider?.game ||
        selectedProvider?.name;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        request?.game;
    } else {
      const fallbackProvider =
        request?.productId ||
        request?.game ||
        request?.provider ||
        request?.name;
      data.provider = fallbackProvider;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        fallbackProvider;
    }

    const gameWindow = window.open("", "_blank");

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: {
          data: buildGameStartPayload({ data, playerBalance }),
        },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) {
          gameWindow.location.href = res.data;
        } else {
          window.location.href = res.data;
        }
      }
    } catch (error) {
      console.error(error);
      if (gameWindow) gameWindow.close();
      alert("เกิดข้อผิดพลาดในการเข้าสู่เกมส์");
    } finally {
      setGameStartLoading(false);
    }
  };

  return (
    <div
      className="games-page-shell"
      style={{
        padding: "18px 0 40px",

        background: "transparent",
        marginTop: 8,
      }}
    >
      <div
        style={{
          margin: "0 8px",
          padding: "24px 16px",
          border: "1px solid #b7e3d8",
          borderRadius: "18px",
          background: "rgba(233, 247, 244, 0.85)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="page-container">
          <div
            className="header-section"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {selectedProvider && (
              <button
                className="back-button"
                onClick={backToProviders}
                style={{
                  background: "transparent",
                  border: "1px solid var(--primaryColor)",
                  color: "var(--primaryColor)",
                  borderRadius: "25px",
                  padding: "8px 16px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  minHeight: "40px",
                }}
              >
                กลับไปหน้าผู้ให้บริการ
              </button>
            )}
          </div>

          {/* Content */}
          {!selectedProvider && (
            <>
              {/* Alert for Restricted Game Categories */}
              {!isTypeAllowed && (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
                      border: "2px solid #EF4444",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "50%",
                        padding: "8px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ fontSize: "24px" }}>🚫</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2
                        style={{
                          color: "white",
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: "600",
                        }}
                      >
                        หมวดหมู่เกมส์ที่ไม่สามารถเล่นได้
                      </h2>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.9)",
                          margin: 0,
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      >
                        เนื่องจากท่านติดเงื่อนไข โปร, เทิร์น หรือกฎของเว็บไซต์
                      </p>
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "20px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        color: "white",
                        fontWeight: "500",
                      }}
                    >
                      ไม่อนุญาต
                    </div>
                  </div>
                </div>
              )}

              {/* Playable providers section */}
              {isTypeAllowed
                ? (() => {
                    const providers = (gameList && gameList.liveCasino) || [];
                    const playable = providers.filter(isProviderAllowed);

                    // Don't show playable section if user has ALLPROVIDER and allgametype
                    const hasAllAccess =
                      providerAllowanceRaw === "ALLPROVIDER" &&
                      typeAllowanceList.includes("allgametype");

                    if (playable.length > 0 && !hasAllAccess) {
                      return (
                        <div style={{ marginBottom: 32 }}>
                          {/* Enhanced Header for Playable Section */}
                          <div
                            className="playable-section"
                            style={{
                              background:
                                "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 90%, transparent) 0%, color-mix(in srgb, var(--primaryColor) 80%, transparent) 100%)",
                              borderRadius: "16px",
                              padding: "16px 24px",
                              marginBottom: 16,
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow:
                                "0 8px 25px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                              border:
                                "2px solid color-mix(in srgb, var(--primaryColor) 60%, transparent)",
                            }}
                          >
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ fontSize: "24px" }}>🎯</span>
                            </div>
                            <div
                              className="content"
                              style={{ flex: 1, textAlign: "center" }}
                            >
                              <h2
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "18px",
                                  fontWeight: "600",
                                }}
                              >
                                เกมส์ที่สามารถเล่นได้
                              </h2>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.9)",
                                  margin: 0,
                                  fontSize: "14px",
                                  marginTop: "4px",
                                }}
                              >
                                คลิกเพื่อเข้าเล่นได้เลย
                              </p>
                            </div>
                            <div
                              style={{
                                marginLeft: "auto",
                                background: "rgba(0, 0, 0, 0.2)",
                                borderRadius: "20px",
                                padding: "6px 12px",
                                fontSize: "12px",
                                color: "white",
                                fontWeight: "500",
                              }}
                            >
                              {playable.length} เกมส์
                            </div>
                          </div>

                          {/* Enhanced Grid for Playable Games */}
                          <div className="responsive-grid">
                            {playable.map((game) => {
                              const img = `https://space.kuga59.co/menu-image/${game?.game}.webp`;
                              const src = img.startsWith("http")
                                ? img
                                : `https://space.kuga59.co/${img}`;
                              return (
                                <div
                                  key={game.productId || game.game}
                                  style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "16px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    border:
                                      "3px solid color-mix(in srgb, var(--primaryColor) 50%, transparent)",
                                    position: "relative",
                                    overflow: "hidden",
                                    boxShadow:
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.05) translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 12px 30px color-mix(in srgb, var(--primaryColor) 40%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "var(--primaryColor)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1) translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "color-mix(in srgb, var(--primaryColor) 50%, transparent)";
                                  }}
                                  onClick={() => handleProviderClick(game)}
                                >
                                  {/* Success Badge */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "8px",
                                      right: "8px",
                                      background:
                                        "linear-gradient(135deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 80%, #000))",
                                      borderRadius: "50%",
                                      width: "32px",
                                      height: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow:
                                        "0 2px 8px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                                      zIndex: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "white",
                                      }}
                                    >
                                      ✓
                                    </span>
                                  </div>

                                  <img
                                    src={src}
                                    alt={game.productName || game.name}
                                    style={{
                                      width: "100%",
                                      objectFit: "contain",
                                      borderRadius: "12px 12px 0 0",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "0",
                                      left: "0",
                                      right: "0",
                                      background:
                                        "linear-gradient(to top, color-mix(in srgb, var(--primaryColor) 90%, transparent), color-mix(in srgb, var(--primaryColor) 70%, transparent))",
                                      color: "white",
                                      padding: "12px",
                                      borderRadius: "0 0 12px 12px",
                                    }}
                                  >
                                    <h3
                                      style={{
                                        fontSize: "14px",
                                        margin: "0",
                                        fontWeight: "600",
                                        textShadow:
                                          "0 1px 2px rgba(0, 0, 0, 0.3)",
                                      }}
                                    >
                                      {game.productName || game.name}
                                    </h3>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    if (playable.length === 0 && !hasAllAccess) {
                      return (
                        <div style={{ marginBottom: 24, marginTop: 12 }}>
                          <div
                            className="restricted-section"
                            style={{
                              background:
                                "linear-gradient(135deg, #6B7280 0%, #4B5563 100%)",
                              borderRadius: "12px",
                              padding: "16px 20px",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow: "0 6px 20px rgba(107, 114, 128, 0.25)",
                              border: "1px solid #6B7280",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Animated background pattern */}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.03) 8px, rgba(255,255,255,0.03) 16px)",
                                animation: "slidePattern 4s linear infinite",
                              }}
                            />

                            {/* Icon container */}
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.12)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 2,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              }}
                            >
                              <span style={{ fontSize: "18px" }}>🔒</span>
                            </div>

                            {/* Content */}
                            <div
                              className="content"
                              style={{
                                flex: 1,
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <h3
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                  textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                                }}
                              >
                                ไม่สามารถเข้าถึงเกมส์ได้
                              </h3>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.85)",
                                  margin: 0,
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                ท่านไม่สามารถเล่นเกมส์ในหมวดหมู่นี้ได้
                                เนื่องจากติดเงื่อนไขการเล่น
                              </p>
                            </div>

                            {/* Status badge */}
                            <div
                              style={{
                                background: "rgba(255, 255, 255, 0.15)",
                                borderRadius: "16px",
                                padding: "4px 10px",
                                fontSize: "11px",
                                color: "white",
                                fontWeight: "500",
                                position: "relative",
                                zIndex: 2,
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                              }}
                            >
                              จำกัด
                            </div>

                            <style>
                              {`
                            @keyframes slidePattern {
                              0% {
                                transform: translateX(-32px);
                              }
                              100% {
                                transform: translateX(0px);
                              }
                            }
                          `}
                            </style>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()
                : null}

              {/* All providers section */}
              <div style={{ marginTop: 16 }}>
                <div className="responsive-grid">
                  {gameList &&
                    gameList.liveCasino &&
                    gameList.liveCasino.length > 0 &&
                    gameList.liveCasino.map((game) => {
                      const img = `https://space.kuga59.co/menu-image/${game?.game}.webp`;
                      const src = img.startsWith("http")
                        ? img
                        : `https://space.kuga59.co/${img}`;
                      return (
                        <div
                          key={game.productId || game.game}
                          style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            border:
                              "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                            position: "relative",
                            overflow: "hidden",
                            boxShadow:
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                            e.currentTarget.style.borderColor =
                              "var(--primaryColor)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)";
                            e.currentTarget.style.borderColor =
                              "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                          }}
                          onClick={() => handleProviderClick(game)}
                        >
                          <img
                            src={src}
                            alt={game.productName || game.name}
                            style={{
                              width: "100%",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <h3
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "0",
                              right: "0",
                              color: "white",
                              fontSize: "14px",
                              margin: "0",
                              padding: "4px 12px",
                              fontWeight: "600",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "0 0 8px 8px",
                            }}
                          >
                            {game.productName || game.name}
                          </h3>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}

          {selectedProvider && (
            <div>
              <div style={{ color: "#060606", marginBottom: 12 }}>
                ผู้ให้บริการ:{" "}
                {selectedProvider?.productName || selectedProvider?.name || "-"}
              </div>
              {isLoadingGames && (
                <div style={{ color: "#aaa" }}>กำลังโหลดรายการเกม...</div>
              )}
              {gamesError && (
                <div style={{ color: "#ef4444" }}>{gamesError}</div>
              )}
              {!isLoadingGames && !gamesError && (
                <div className="responsive-grid">
                  {providerGames && providerGames.length > 0 ? (
                    providerGames.map((g) => (
                      <div
                        key={g.gameId || g.id || `${g.providerId}-${g.code}`}
                        style={{
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          border:
                            "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                          position: "relative",
                          overflow: "hidden",
                          boxShadow:
                            "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                          e.currentTarget.style.borderColor =
                            "var(--primaryColor)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)";
                          e.currentTarget.style.borderColor =
                            "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                        }}
                        onClick={() => getGameLink(g)}
                      >
                        <img
                          src={
                            g.image ||
                            (g.img && `${g.img}`) ||
                            (g.logo && `https://space.kuga59.co/${g.logo}`) ||
                            `https://space.kuga59.co/${g.image}`
                          }
                          alt={g.gameName || g.name || g.title || "game"}
                          style={{
                            width: "100%",
                            objectFit: "contain",
                            borderRadius: "8px",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <h3
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            right: "0",
                            color: "white",
                            fontSize: "14px",
                            margin: "0",
                            padding: "4px 12px",
                            fontWeight: "600",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "0 0 8px 8px",
                          }}
                        >
                          {g.gameName || g.name || g.title || "Game"}
                        </h3>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: "#aaa" }}>
                      ไม่พบเกมสำหรับผู้ให้บริการนี้
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Optionally show a simple overlay while starting game */}
          {gameStartLoading && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              กำลังเริ่มเกม...
            </div>
          )}

          {/* Toast Notification */}
          <Toast
            message={toast.message}
            type={toast.type}
            show={toast.show}
            onClose={hideToast}
          />

          <style jsx>{`
            .page-container {
              padding: 24px;
            }

            @media (max-width: 768px) {
              .page-container {
                padding: 0;
              }
            }

            @media (min-width: 768px) {
              .page-container {
                padding: 32px;
              }
            }

            .responsive-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 16px;
              margin-top: 20px;
            }

            @media (min-width: 768px) {
              .responsive-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }

            @media (min-width: 1200px) {
              .responsive-grid {
                grid-template-columns: repeat(5, 1fr);
              }
            }

            @media (max-width: 768px) {
              .games-page-shell {
                padding: 0 0 40px !important;
              }

              .playable-section,
              .restricted-section {
                padding: 12px 16px !important;
                flex-direction: column !important;
                text-align: center !important;
                gap: 8px !important;
              }

              .playable-section .icon-container,
              .restricted-section .icon-container {
                width: 32px !important;
                height: 32px !important;
                padding: 6px !important;
              }

              .playable-section .content h2,
              .restricted-section .content h3 {
                font-size: 16px !important;
              }

              .playable-section .content p,
              .restricted-section .content p {
                font-size: 12px !important;
              }

              .header-section {
                flex-direction: column !important;
                align-items: flex-start !important;
                gap: 16px !important;
              }

              .page-title {
                font-size: 24px !important;
                width: 100% !important;
                justify-content: center !important;
              }

              .back-button {
                width: 100% !important;
                text-align: center !important;
                padding: 12px 16px !important;
              }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default LiveCasino;
